import { useCallback, useEffect, useRef, useState } from "react";

/** Most thumbnail iframes allowed to be loading at any one moment. */
const MAX_CONCURRENT = 3;

/** How long a card may hold its slot before it is reclaimed regardless. */
const LOAD_TIMEOUT_MS = 15_000;

/**
 * Module-level semaphore bounding how many Looker Studio embeds load at once — a resource guard,
 * not a latency fix. Never re-tune MAX_CONCURRENT against load times: the superlinear figures
 * behind it binned latency by a measure derived from loadMs itself, and a duration-independent
 * measure reverses the sign (SIM-2911, SIM-2858). A held slot is not a claim the thumbnail painted.
 */
interface QueueEntry {
  priority: number;
  activate: () => void;
}

const waiting: QueueEntry[] = [];
let inFlight = 0;

function admit() {
  waiting.sort((a, b) => a.priority - b.priority);
  while (inFlight < MAX_CONCURRENT) {
    const next = waiting.shift();
    if (!next) return;
    inFlight += 1;
    next.activate();
  }
}

function enqueue(entry: QueueEntry) {
  waiting.push(entry);
  admit();
}

function dequeue(entry: QueueEntry) {
  const index = waiting.indexOf(entry);
  if (index !== -1) waiting.splice(index, 1);
}

function releaseSlot() {
  if (inFlight > 0) inFlight -= 1;
  admit();
}

type SlotState = "idle" | "waiting" | "inflight" | "settled";

/**
 * Hook for a single DashboardItem thumbnail. Both returned callbacks free the card's slot;
 * only `onIframeLoad` reveals the iframe.
 * @param priority - The card's index in the rendered list. Admission follows this order so
 *   the load sequence matches the on-screen sort (starred first).
 */
export function useThumbnailQueue(priority: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // `entry` is the queue identity, so an unmount can splice this card back out
  // by reference; `state` makes release idempotent, since load/error and the
  // timeout race and either may arrive first.
  const slot = useRef<{
    entry: QueueEntry | null;
    state: SlotState;
    timer: ReturnType<typeof setTimeout> | null;
  }>({ entry: null, state: "idle", timer: null });

  const settle = useCallback(() => {
    const current = slot.current;
    if (current.state !== "inflight") return;
    current.state = "settled";
    if (current.timer !== null) {
      clearTimeout(current.timer);
      current.timer = null;
    }
    releaseSlot();
  }, []);

  useEffect(() => {
    if (slot.current.state !== "idle") return;
    const el = containerRef.current;
    if (!el) return;

    const join = () => {
      const queueEntry: QueueEntry = {
        priority,
        activate: () => {
          const current = slot.current;
          current.state = "inflight";
          current.entry = null;
          current.timer = setTimeout(settle, LOAD_TIMEOUT_MS);
          setShouldLoad(true);
        },
      };
      slot.current.entry = queueEntry;
      slot.current.state = "waiting";
      enqueue(queueEntry);
    };

    // Without IntersectionObserver there is no visibility gate, so every card
    // queues on mount; the concurrency cap is what keeps that from stampeding.
    if (typeof IntersectionObserver === "undefined") {
      join();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || slot.current.state !== "idle") return;
        observer.disconnect();
        join();
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [priority, settle]);

  // Unmount (filter, re-sort, navigation) destroys the iframe, so the card must
  // stop occupying the queue: drop it if still waiting, free its slot if in flight.
  useEffect(
    () => () => {
      const current = slot.current;
      if (current.state === "waiting" && current.entry) {
        dequeue(current.entry);
        current.entry = null;
        current.state = "settled";
        return;
      }
      settle();
    },
    [settle],
  );

  const onIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    settle();
  }, [settle]);

  // A failed embed frees its slot immediately and keeps the placeholder visible.
  const onIframeError = useCallback(() => {
    settle();
  }, [settle]);

  return { containerRef, shouldLoad, iframeLoaded, onIframeLoad, onIframeError };
}
